/**
 * 파일 업로드 상태 플로팅 모달 styles — 레거시 `.portal-modal` CSS 1:1 매핑.
 * 추정 금지. 사용자 제공 CSS 그대로 옮김.
 *
 * 마크업·atom·index.tsx 동작은 건드리지 않고 styled 토큰만 갈아끼움.
 */
import { Box, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';

// .portal-modal + .modal-inner 합쳐서 단일 root.
// (레거시 마크업의 두 단계 div 을 한 styled Box 에 압축 — 시각/박스 모델 동일)
export const ModalRoot = styled(Box)({
  position: 'fixed',
  bottom: 10,
  right: 60,
  zIndex: 1500,
  width: 400,
  maxWidth: '100vw',
  display: 'flex',
  flexDirection: 'column',
  background: 'transparent',
  overflow: 'hidden',
  borderRadius: 8,
  boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.12), 0 16px 16px 0 rgba(0, 0, 0, 0.12)',
  WebkitBackdropFilter: 'blur(10px)',
  backdropFilter: 'blur(10px)',
  '@media screen and (max-width: 479px)': {
    display: 'none',
  },
});

// .modal-header — 헤더 자체가 폴드 토글 영역. <button> nesting 회피 위해 div 유지.
export const Header = styled('div')({
  width: '100%',
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  fontSize: 16,
  lineHeight: 1.5,
  fontWeight: 500,
  color: 'var(--dk-text, #000)',
  background: 'rgba(249, 250, 251, 0.90)',
  borderBottom: '1px solid var(--dk-line, #e5e5e5)',
  WebkitBackdropFilter: 'blur(8px)',
  backdropFilter: 'blur(8px)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  userSelect: 'none',
  '&:focus-visible': {
    outline: '2px solid #5B9FE3',
    outlineOffset: -2,
  },
});

// .modal-header .btn — chevron / close 공통
const headerBtnBase = {
  display: 'flex',
  alignItems: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1,
  color: 'inherit',
} as const;

// .btn-fold (chevron 컨테이너)
export const HeaderIconButton = styled('span')({
  ...headerBtnBase,
  flexShrink: 0,
  '& > img': {
    width: 14,
    height: 14,
    display: 'block',
  },
});

// .modal-title
export const HeaderTitle = styled(Typography)({
  flex: 1,
  marginLeft: 12,
  fontSize: 16,
  lineHeight: 1.5,
  fontWeight: 500,
  color: 'var(--dk-text, #000)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// .btn-close
export const CloseButton = styled('button')({
  ...headerBtnBase,
  flexShrink: 0,
  '& > img': {
    width: 14,
    height: 14,
    display: 'block',
  },
  '&:focus-visible': {
    outline: '2px solid #5B9FE3',
    outlineOffset: 2,
    borderRadius: 4,
  },
});

// .modal-body (collapsed wrap 통합 — collapsed 시 index.tsx 가 이 영역 자체 렌더 안 함)
export const ItemsScroll = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  gap: 10,
  padding: 12,
  width: '100%',
  background: 'var(--dk-bg, rgba(255, 255, 255, 1))',
  WebkitBackdropFilter: 'blur(8px)',
  backdropFilter: 'blur(8px)',
  border: 0,
  minHeight: 0,
  maxHeight: 250,
  overflowY: 'auto',
});

// .activity-log-item
export const ItemRow = styled(Box)({
  marginTop: 10,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  height: 60,
  '&:first-of-type': {
    marginTop: 0,
  },
});

// .item-preview
export const ThumbBox = styled(Box)({
  width: 48,
  height: 48,
  minWidth: 48,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  '& > img.preview': {
    // 사용자 CSS 는 max-width 만 명시. max-height 가 누락되면 세로로 긴 이미지가
    // 부모 48px 박스를 넘어 잘려 가운데 흰 영역만 보이는 케이스 발생 → max-height 48 추가.
    maxWidth: 48,
    maxHeight: 48,
    objectFit: 'contain',
  },
  '& > img.icon': {
    maxWidth: 48,
    maxHeight: 48,
    objectFit: 'contain',
  },
});

// .activity-log-item-detail
export const ItemMeta = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

// .attachment-item-name
export const ItemName = styled(Typography)({
  fontSize: 13,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--dk-text, #000)',
});

// .log-message
export const ItemMessage = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: 'error' | 'muted' }>(({ tone }) => ({
  lineHeight: 1.3,
  color: tone === 'error' ? '#F55060' : '#888D96',
  wordBreak: 'keep-all',
  fontSize: 12,
}));

// .activity-log-status — 성공/실패 색은 SuccessIcon/ErrorIcon 의 mask color 로 직접 적용됨
export const StatusIconBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flexShrink: 0,
});

/**
 * SVG가 stroke="currentColor"라 `<img>`로는 색이 안 먹어 mask-image로 색을 입힌다.
 * background-color가 곧 아이콘 색.
 */
const maskedIcon = (url: string, color: string) => ({
  width: 20,
  height: 20,
  backgroundColor: color,
  WebkitMaskImage: `url(${url})`,
  maskImage: `url(${url})`,
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
});

export const SuccessIcon = styled('span')(
  maskedIcon('/icons/approval/check-circle.svg', '#43B16F'),
);

export const ErrorIcon = styled('span')(
  maskedIcon('/icons/approval/exclamation-circle.svg', '#F55060'),
);

// .portal-modal .spinner — border-top-color rgba(0,102,204,1)
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const SpinnerRing = styled('span')({
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '2px solid var(--dk-line-strong, #DDD)',
  borderTopColor: 'var(--dk-accent, rgba(0, 102, 204, 1))',
  animation: `${spin} 0.8s linear infinite`,
});
